/* Publishing the next tolerance version, as a diff against the one in force.
 *
 * **A diff rather than a fresh form, and that is a safety decision rather than a convenience.** A
 * retune is almost always one bound in an otherwise settled list; a blank form would make somebody
 * retype the bounds they are keeping, on the one screen where a mistype is permanent. Every past run
 * keeps citing the version that judged it, so a published bound is history rather than a setting.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ToleranceVersion } from '@/shared/api/generated/reconciliation';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';

import { usePublishTolerances } from '../api/governance';
import { BOUND_RULES, diffRows, diffToTolerances, nextVersion } from '../model/authoring';

export function PublishToleranceDialog({
  open,
  onClose,
  definitionId,
  current,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly definitionId: string;
  readonly current: ToleranceVersion;
}): ReactElement {
  const { t } = useTranslation();
  const publish = usePublishTolerances(definitionId);
  const [bounds, setBounds] = useState<Record<string, string>>({});

  const rows = diffRows(current, bounds);
  const outcome = diffToTolerances(rows);
  const problemKey = 'problemKey' in outcome ? outcome.problemKey : null;
  const version = nextVersion(current);

  function close(): void {
    setBounds({});
    publish.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      title={t('reconciliation.publish.title', { version })}
      onClose={close}
      className="lq-rc__publish"
    >
      <div className="lq-band lq-band--warn">
        <div className="lq-band__title">
          {t('reconciliation.publish.immutable.title')}
        </div>
        <div className="lq-band__body">
          {t('reconciliation.publish.immutable.body', {
            next: version,
            current: current.version,
          })}
        </div>
      </div>

      <div className="lq-rc__formLabel">
        {t('reconciliation.publish.diffAgainst', { version: current.version })}
      </div>

      {rows.map((row) => (
        <div key={row.pair} className={`lq-rc__diff lq-rc__diff--${row.change}`}>
          <div className="lq-rc__diffHead">
            <span className="lq-mono">{row.pair}</span>
            <span className={`lq-rc__diffTag lq-rc__diffTag--${row.change}`}>
              {t(`reconciliation.publish.${row.change}`)}
            </span>
          </div>
          <div className="lq-rc__diffBody">
            <span className="lq-muted">
              {row.previous === null
                ? t('reconciliation.publish.noBound')
                : String(row.previous)}
            </span>
            {row.change === 'changed' && (
              <>
                <span aria-hidden="true"> → </span>
                <strong>{row.next}</strong>
              </>
            )}
          </div>
          {BOUND_RULES[row.kind].required && (
            <input
              type="number"
              className="lq-rc__mono lq-rc__diffInput"
              aria-label={t('reconciliation.publish.boundFor', { pair: row.pair })}
              value={row.next}
              onChange={(event) => {
                setBounds({ ...bounds, [row.pair]: event.target.value });
              }}
            />
          )}
        </div>
      ))}

      {problemKey !== null && <p className="lq-rc__error">{t(problemKey)}</p>}
      {publish.isError && <p className="lq-rc__error">{publish.error.message}</p>}

      <div className="lq-rc__actions">
        <Button tone="secondary" onClick={close}>
          {t('reconciliation.publish.cancel')}
        </Button>
        <Button
          tone="primary"
          onClick={() => {
            if (!('tolerances' in outcome)) return;
            publish.mutate(
              { nextVersion: version, tolerances: outcome.tolerances },
              { onSuccess: close },
            );
          }}
          disabled={problemKey !== null || publish.isPending}
        >
          {t('reconciliation.publish.confirm', { version })}
        </Button>
      </div>
    </Modal>
  );
}
